/**
 * PrimaryCredentialStep Component
 *
 * Step 1 of enrollment: Register primary biometric credential with PRF extension.
 * This is the ONLY required step - all others are optional.
 *
 * FERP-PII-052: Primary credential captures PRF output for L1/L1G encryption.
 */

import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  TextInput,
  Button,
  Alert,
  Group,
  List,
  ThemeIcon,
  Progress,
  Box,
} from '@mantine/core';
import {
  IconFingerprint,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useAuth } from '../../auth/AuthContext';
import { webAuthnRepository } from '@ferp/workflow-forms';

/**
 * Check if browser supports WebAuthn
 * Native implementation to avoid external dependency
 */
function browserSupportsWebAuthn(): boolean {
  return (
    window?.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function'
  );
}

export interface PrimaryCredentialStepProps {
  /** Called when step is complete */
  onComplete: (credentialId?: string, prfOutput?: string, deviceName?: string) => void;

  /** Called when user skips enrollment */
  onSkip: () => void;

  /** Allow skipping */
  allowSkip: boolean;

  /** Loading state from parent */
  loading: boolean;
}

/**
 * Detect platform authenticator (Touch ID, Face ID, Windows Hello)
 */
async function checkPlatformAuthenticator(): Promise<boolean> {
  if (!browserSupportsWebAuthn()) {
    return false;
  }
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Get device name from user agent
 */
function getDefaultDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Android/.test(ua)) return 'Android Device';
  return 'Unknown Device';
}

export const PrimaryCredentialStep: React.FC<PrimaryCredentialStepProps> = ({
  onComplete,
  onSkip,
  allowSkip,
  loading,
}) => {
  const { user, tenant } = useAuth();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [prfSupported, setPrfSupported] = useState<boolean | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check platform authenticator and PRF support
  useEffect(() => {
    const checkSupport = async () => {
      const platformAvailable = await checkPlatformAuthenticator();
      setSupported(platformAvailable);

      if (platformAvailable) {
        setDeviceName(getDefaultDeviceName());
        // Check PRF support (best-effort check)
        const prfAvailable = await webAuthnRepository.isPrfSupported();
        setPrfSupported(prfAvailable);
      }
    };
    checkSupport();
  }, []);

  /**
   * Handle biometric registration with PRF extension
   *
   * Flow:
   * 1. Call webAuthnRepository.registerPrfCredential(credentialName)
   * 2. Repository handles challenge/response flow with backend
   * 3. On success, pass credentialId + prfOutput to onComplete
   * 4. Parent component sends prfOutput to backend for escrow creation
   */
  const handleRegister = async () => {
    if (!user?.id || !tenant?.id) {
      setError('User or tenant context not available. Please log in again.');
      return;
    }

    setRegistering(true);
    setError(null);

    try {
      // Call the WebAuthn repository with credential name
      // Repository handles challenge/response internally
      const credentialName = deviceName.trim() || getDefaultDeviceName();
      const result = await webAuthnRepository.registerPrfCredential(credentialName);

      if (!result.success) {
        throw new Error('Registration failed');
      }

      // Pass the credential ID and PRF output to the parent
      // The PRF output will be used to create verification escrows
      onComplete(
        result.credentialId,
        result.prfOutput, // 32-byte key material for L1/L1G encryption
        result.deviceName || credentialName
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
    } finally {
      setRegistering(false);
    }
  };

  // Still checking support
  if (supported === null) {
    return (
      <Stack gap="md" mt="md">
        <Text>Checking device capabilities...</Text>
        <Progress value={100} animated />
      </Stack>
    );
  }

  // Not supported
  if (!supported) {
    return (
      <Stack gap="md" mt="md">
        <Alert color="red" icon={<IconX size={16} />} title="Not Supported">
          <Text size="sm">
            Your device does not support biometric authentication (Touch ID, Face ID, Windows Hello).
            You may need to use a different device to set up biometric protection.
          </Text>
        </Alert>

        {allowSkip && (
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onSkip}>
              Skip for Now
            </Button>
          </Group>
        )}
      </Stack>
    );
  }

  // PRF not supported (device supports biometrics but not PRF)
  if (prfSupported === false) {
    return (
      <Stack gap="md" mt="md">
        <Alert color="orange" icon={<IconAlertCircle size={16} />} title="PRF Extension Not Supported">
          <Text size="sm">
            Your device supports biometric authentication but does not support the PRF extension
            required for user-controlled encryption.
          </Text>
          <Text size="sm" mt="xs">
            This feature requires:
          </Text>
          <List size="sm" spacing="xs" mt="xs">
            <List.Item>macOS: Safari 16.4+ or Chrome 109+ with Touch ID</List.Item>
            <List.Item>Windows: Edge/Chrome 109+ with Windows Hello</List.Item>
            <List.Item>iOS: Safari 16.4+ with Face ID/Touch ID</List.Item>
            <List.Item>Android: Chrome 109+ with Fingerprint/Face unlock</List.Item>
          </List>
        </Alert>

        {allowSkip && (
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onSkip}>
              Skip for Now
            </Button>
          </Group>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="md" mt="md">
      {/* Success indicator */}
      <Alert color="green" icon={<IconCheck size={16} />}>
        <Text fw={500}>Device Ready</Text>
        <Text size="sm" mt={4}>
          Your device supports biometric authentication. Proceed to register your fingerprint or face.
        </Text>
      </Alert>

      {/* Benefits list */}
      <Alert color="blue" icon={<IconShieldCheck size={16} />} title="What This Protects">
        <List size="sm" spacing="xs" mt="xs">
          <List.Item
            icon={
              <ThemeIcon color="blue" size={20} radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
            }
          >
            Social Security Number (SSN)
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="blue" size={20} radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
            }
          >
            Bank account and routing numbers
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="blue" size={20} radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
            }
          >
            Tax identification numbers (TIN)
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="blue" size={20} radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
            }
          >
            Credit card information
          </List.Item>
        </List>
        <Text size="xs" c="dimmed" mt="md">
          Only YOU can access this data - even administrators cannot view it without your biometric.
        </Text>
      </Alert>

      {/* Device name input */}
      <TextInput
        label="Device Name (Optional)"
        placeholder="My iPhone"
        value={deviceName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeviceName(e.target.value)}
        description="Name this device for easy identification"
      />

      {/* Error display */}
      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Registration Failed">
          {error}
        </Alert>
      )}

      {/* Registration progress */}
      {registering && (
        <Box>
          <Text size="sm" mb="xs">
            Waiting for biometric scan...
          </Text>
          <Progress value={100} animated />
        </Box>
      )}

      {/* Action buttons */}
      <Group justify="space-between" mt="md">
        {allowSkip && (
          <Button variant="subtle" onClick={onSkip} disabled={registering || loading}>
            Remind Me Later
          </Button>
        )}
        <Button
          onClick={handleRegister}
          loading={registering || loading}
          leftSection={<IconFingerprint size={18} />}
        >
          Register Biometric
        </Button>
      </Group>
    </Stack>
  );
};
